import { defineComponent as _defineComponent } from 'vue'
import { computed, inject } from 'vue';

import { useLanguageStore, useUserStore, useTrackingStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { IN_LISTS_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import { usePromotion } from '@/helpers/composables/usePromotion';

import { OfferLocation } from '@/helpers/offer-helper';
import { SraPlacement, type Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { getUserLocationCountryCode } from '@/helpers/geo-location-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { freeTrialSwiperRules } from '@/helpers/promotion-helper';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';

import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { isMxSqtvActive } from '@/components/experiments/SponsoredSQTVHacks/mxSqtvSR';
import { isBrSqtvActive } from '@/components/experiments/SponsoredSQTVHacks/brSqtvSR';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import { useATVPlacementsExp } from '@/components/experiments/ATVPlacementsExp';
import { useTitleBasedSteering } from '@/helpers/ad/useCrunchyrollPromotion';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import { useAdServer, attachAdServerContexts } from '@/helpers/composables/useAdServer';
import adminConfig from '@/features/promotions/config.json';
import { NewBuyBoxView } from '@/stores/user.store';
import { useBingeAuPromo } from '@/helpers/composables/useBingeAUPromo';

interface PromotionItem {
	id: string;
	type: 'sqtv' | 'qtv' | 'sr' | 'ft';
}

/*
 * Promotion Items for Swiper
 * Collect all active promotions to display as slides
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionPlacement',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
};

const AdServerPromotionPlacement = () => import('@/features/buybox/components/list/AdServerPromotionPlacement.vue');
const ListPromotionRow = () => import('@/features/buybox/components/list/ListPromotionRow.vue');
const QualityTV = () => import('@/features/buybox/components/list/QualityTV.vue');
const SingleQTV = () => import('@/features/buybox/components/list/SingleQTV.vue');
const PPExpSwiper = () => import('@/components/experiments/PPExperiments/Components/PPExpSwiper.vue');
const FreeTrialSwiperOS = () => import('@/features/buybox/components/grid/FreeTrialSwiperOS.vue');
const PromotionSwiper = () => import('@/features/buybox/components/list/PromotionSwiper.vue');



const { isProUser } = useUserStore();
const { isArbitrating } = useArbitrage();
const { country } = useLanguageStore();
const { pagesVisited } = useTrackingStore();



const { hardcodedShortName: hardcodedShortNameTitleSteering, hasTitleBasedSteering } = useTitleBasedSteering({
	title: () => props.title,
});

const { promotionOffer, catalogueItem, inPromotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
	hardcodedShortName: hardcodedShortNameTitleSteering,
});

const inLists = inject(
	IN_LISTS_KEY,
	computed(() => [])
);

const activateLowPerfExp = computed(
	() =>
		inLists.value.includes('EXP_LOWPERF_URL_SWIPER') &&
		!props.offers.some(offer => offer.package.shortName === 'hlu')
);
const { activeVariantWithControlGroup: lowPerfExpVariant } = useActivateExperiment(
	'EXP_LOWPERF_URL_SWIPER_2',
	activateLowPerfExp
);

const activatePrimeSQTV = computed(
	() => !props.offers.some(offer => offer.package.shortName === 'amp') && !inPromotionCatalogue.value
);

const { activeVariantWithControlGroup: primeSQTVExpVariant } = useActivateExperiment(
	'EXP_PRIME_SQTV',
	activatePrimeSQTV
);

const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });

// ** SQTV SR **

const { sponsoredAd, isShadowCampaign, hasActiveSRCampaign } = useSendBidId({
	pageType: 'VIEW_BUYBOX',
	placement: SraPlacement.Buybox,
	jwEntityID: props.title.id,
	isSeo: pagesVisited.value <= 1,
});

const seoCampaignConfig = computed(() => (adminConfig?.sr_sqtv_autoswipe?.seo ?? {}) as Record<string, number>);
const clientCampaignConfig = computed(() => (adminConfig?.sr_sqtv_autoswipe?.client ?? {}) as Record<string, number>);

const isSeoCampaignFromConfig = computed(() => {
	const campaignName = sponsoredAd.value?.campaign?.name;

	return Object.keys(seoCampaignConfig.value).includes(campaignName || '');
});

const isClientCampaignFromConfig = computed(() => {
	const campaignName = sponsoredAd.value?.campaign?.name;

	return Object.keys(clientCampaignConfig.value).includes(campaignName || '');
});

const autoSwipeDelay = computed(() => {
	if (!hasActiveSRCampaign.value) return undefined;

	const campaignName = sponsoredAd.value?.campaign?.name;

	if (isSeoCampaignFromConfig.value && campaignName) {
		return seoCampaignConfig.value[campaignName];
	}
	if (isClientCampaignFromConfig.value && campaignName) {
		return clientCampaignConfig.value[campaignName];
	}

	// SEO campaign should take 1s before auto swiping
	// campaign delivery estimates showed that the Client part alone is enough
	if (pagesVisited.value <= 1) return 1 * 1000;

	// no delay for the Client campaign
	return 1 * 1000;
});

const qtvAdditionalContexts = computed(() => props.additionalContexts.concat(props.trackingContexts));

// PP Experiments - active them all here
const activatePPExperiments = computed(
	() => [9, 119, 283].includes(promotionOffer.value?.package.packageId || 0) && !isArbitrating.value
);

const autoSwipeSrCampaign = computed(() => {
	const userCountry = getUserLocationCountryCode();

	// Geo targeted SR campaigns for SQTV
	const srCampaignCountries: Record<string, string> = {
		BLACK_FRIDAY_DACH_AT_SQTV_CLIENT_BURST2_SRA: 'AT',
		BLACK_FRIDAY_DACH_CH_SQTV_CLIENT_BURST2_SRA: 'CH',
	};
	const campaignName = sponsoredAd.value?.campaign?.name;

	// App Only Campaigns i.e. /de would get a campaign returned even outside geo targeting
	const alwaysAutoSwipeAppOnlyCampaigns = [
		'BLACK_FRIDAY_DACH_AT_SQTV_SEO_BURST2_SRA',
		'BLACK_FRIDAY_DACH_CH_SQTV_SEO_BURST2_SRA',
	];

	if (alwaysAutoSwipeAppOnlyCampaigns.includes(campaignName)) return true;

	// keeping these separate in case they require different handling in future
	if (isSeoCampaignFromConfig.value) return true;
	if (isClientCampaignFromConfig.value) return true;

	const currentCampaignCountry = srCampaignCountries[campaignName];

	if (!campaignName || !userCountry) return false;

	return currentCampaignCountry === userCountry;
});

const activateMxSqtvSr = computed(() => isMxSqtvActive(props.title, country.value));
const mxSqtvSR = useActivateExperiment('HACK_SQTV_SR_MX_2', activateMxSqtvSr);

const activateBrSqtvSr = computed(() => isBrSqtvActive(props.title, country.value));
const brSqtvSR = useActivateExperiment('HACK_SQTV_SR_BR_2', activateBrSqtvSr);

// Double Stack Experiment
const { activeVariantWithControlGroup: doubleStackExpVariant } = useExperiment('DOUBLE_STACK_EXP_3');

const { hardcodedShortNameDSExp } = useDoubleStackExp();
// Double Stack Experiment

// ATV Placements Experiment

const activateAtvPlacementsExp = computed(
	() =>
		promotionOffer.value?.package.shortName !== 'hlu' &&
		!props.offers.some(offer => offer.package.shortName === 'atp')
);

const { activeVariantWithControlGroup: atvPlacementsExpVariant } = useActivateExperiment(
	'ATV_PLACEMENTS_EXP',
	activateAtvPlacementsExp
);

const { atvPlacementsExpPlacement } = useATVPlacementsExp();

// ATV Placements Experiment

const activateSwiperBrowserExp = computed(() => promotionOffer.value?.package.shortName !== 'hlu');
const { activeVariantWithControlGroup: swiperBrowserExpVariant } = useActivateExperiment(
	'SWIPER_BROWSER_EXP_2',
	activateSwiperBrowserExp
);
// Swiper Browser Experiment

// AU Binge Promo
const { isBingeAuPromoTitle } = useBingeAuPromo({
	title: () => props.title,
});
//

const freeTrialActive = computed(() => {
	if (isProUser.value) return false;
	if (atvPlacementsExpVariant.value) return atvPlacementsExpPlacement.value === 'ft';
	if (swiperBrowserExpVariant.value) return swiperBrowserExpVariant.value === 'control';
	if (lowPerfExpVariant.value) return lowPerfExpVariant.value === 'control';
	if (hasTitleBasedSteering.value) return true;
	if (primeSQTVExpVariant.value) return primeSQTVExpVariant.value === 'control';
	if (isBingeAuPromoTitle.value) return false;

	return catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && props.showFreeTrial);
});

/* Override the config provider shortName  */
const hardcodedFTShortName = computed(() => {
	if (atvPlacementsExpPlacement.value === 'ft') return 'atp';
	if (hardcodedShortNameTitleSteering.value) return hardcodedShortNameTitleSteering.value;
	if (hardcodedShortNameDSExp.value) return hardcodedShortNameDSExp.value;
	if (primeSQTVExpVariant.value === 'control') return 'amp';
	return null;
});

const hardcodedQTVShortName = computed(() => {
	if (atvPlacementsExpPlacement.value === 'qtv') return 'atp';

	return null;
});

const hardcodedSQTVShortName = computed(() => {
	if (primeSQTVExpVariant.value === 'variant_1') return 'amp';
	if (atvPlacementsExpPlacement.value === 'sqtv') return 'atp';
	// return catalogue short name here when no override is provided, to pass to isSQTVEmpty check

	if (isBingeAuPromoTitle.value) return 'bng';
	return catalogueItem.value?.shortName || null;
});

const hideTVODQTV = computed(
	() =>
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		props.isFreeBuyBox
);

// SQTV active logic
const isSingleQTVActive = computed(() => {
	if (swiperBrowserExpVariant.value || hasTitleBasedSteering.value) return false;

	if (isProUser.value) return false;

	if (primeSQTVExpVariant.value) return primeSQTVExpVariant.value === 'variant_1';

	if (atvPlacementsExpVariant.value) return atvPlacementsExpPlacement.value === 'sqtv';

	if (isBingeAuPromoTitle.value) return true;

	return catalogueItem.value?.placement === 'sqtv';
});

const isSponsoredSqtvActive = computed(
	() =>
		mxSqtvSR.activeVariantWithControlGroup.value === 'control' ||
		brSqtvSR.activeVariantWithControlGroup.value === 'control'
);

const qualityTvActive = computed(() => {
	if (doubleStackExpVariant.value || swiperBrowserExpVariant.value || primeSQTVExpVariant.value) return false;

	// ATV Placements Exp
	if (atvPlacementsExpVariant.value) return atvPlacementsExpPlacement.value === 'qtv';
	// ATV Placements Exp

	// CR steering US
	if (hasTitleBasedSteering.value) return false;
	// CR steering US

	return (
		!isProUser.value &&
		// get qtv placement from config
		catalogueItem.value?.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const freeTrialSwiperActive = computed(() => {
	if (isProUser.value) return false;
	if (swiperBrowserExpVariant.value) return swiperBrowserExpVariant.value !== 'control';
	if (!freeTrialSwiperRules) return false;
	// Experiments
	if (
		isOverProviderTitle.value ||
		doubleStackExpVariant.value ||
		atvPlacementsExpVariant.value ||
		lowPerfExpVariant.value ||
		primeSQTVExpVariant.value
	)
		return false;

	// ATV FreeTrials take precedence
	if (catalogueItem.value?.shortName === 'atp') return false;

	return freeTrialSwiperRules[country.value] != null;
});

/*
 * Ad Server Mutation
 * Calculate what placement would be shown and send to ad server
 * This informs the ad server about our intent for ALL countries
 */
const adServerPlacement = computed(() => {
	if (isSingleQTVActive.value) return 'sqtv';
	if (qualityTvActive.value) return 'qtv';
	if (freeTrialSwiperActive.value) return 'swiper';
	if (freeTrialActive.value) return 'ft';

	return null;
});

const adServerProviders = computed(() =>
	adServerPlacement.value === 'swiper' ? freeTrialSwiperRules[country.value] : [catalogueItem.value?.shortName]
);

// Call ad server mutation here (for all countries)
// Only PL will get a response back, others will fall through to standard logic

const titleSourceId = inject<string | null>('titleSourceId', null);
const {
	placementFromAdServer,
	shortNameFromAdServer,
	hasPromotionFromAdServer,
	isValidAdServerPlacement,
	adServerRenderAction,
} = useAdServer({
	placement: adServerPlacement,
	providers: adServerProviders,
	titleSourceId: titleSourceId,
	enabled: () => !!props.title,
});

/*
 * Short-circuit Ad Server Promotion Rendering
 * Conditions where we do not want to render Ad Server Promotions even if it is an active country and has a valid promotion
 * 1. Sponsored Recommendations - to avoid conflicts with SR campaigns
 * 2. Experiment - until we have full coordination between Ad Server and Experiments
 */
const overrideAdServerPlacement = computed(() => {
	// exclude SRs
	if (sponsoredAd.value?.campaign) return true;
	// exclude Experiments
	if (swiperBrowserExpVariant.value) return true;

	return false;
});

const adServerFlexContextValue = computed(() =>
	adServerRenderAction.value === 'enabled' && overrideAdServerPlacement.value
		? 'override'
		: adServerRenderAction.value
);

const renderAdServerPromotion = computed(() => {
	if (isProUser.value) return false;

	// Use this boolean if you want to disable Ad Server Promotions for experiments, SRs or other conditions
	// We need to pass this information to Snowplow for proper tracking
	if (overrideAdServerPlacement.value) return false;

	// this check is already done in the useAdServer composable, but expilitly calling it here for visibility
	if (!isValidAdServerPlacement(adServerPlacement.value)) return false;
	return hasPromotionFromAdServer.value;
});

// Now that we have final isSingleQTVActive and hardcodedSQTVShortName, we can use them
const { isSQTVEmpty } = useSingleQTV(() => props.title, hardcodedSQTVShortName, isSingleQTVActive);

const showSQTV = computed(() => {
	// CR steering US
	if (doubleStackExpVariant.value || hasTitleBasedSteering.value) return false;
	// CR steering US

	return isSingleQTVActive.value && !isSQTVEmpty.value;
});

const promotionItems = computed((): PromotionItem[] => {
	const items: PromotionItem[] = [];

	// Add promotions in priority order (based on original v-else-if chain)
	if (showSQTV.value) {
		items.push({ id: 'sqtv', type: 'sqtv' });
	}
	if (qualityTvActive.value) {
		items.push({ id: 'qtv', type: 'qtv' });
	}
	if (freeTrialActive.value) {
		items.push({ id: 'ft', type: 'ft' });
	}

	if (sponsoredAd.value?.campaign && !swiperBrowserExpVariant.value && !primeSQTVExpVariant.value) {
		items.push({ id: 'sr', type: 'sr' });
	}

	return items;
});
const hideSwiperControls = computed(() => {
	// Hide swiper controls for Pluribus SQTV SR campaign in US
	if (
		(sponsoredAd.value?.campaign?.name === 'PLURIBUS_CLIENT-USER_US_BURST2_SRA' ||
			sponsoredAd.value?.campaign.name === 'PLURIBUS_US_SEO-USER_BURST2_SEO_SRA') &&
		country.value === 'US'
	) {
		return true;
	}
	return false;
});

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,AdServerPromotionPlacement, ListPromotionRow, QualityTV, SingleQTV, PPExpSwiper, FreeTrialSwiperOS, PromotionSwiper, props, isProUser, isArbitrating, country, pagesVisited, emit, hardcodedShortNameTitleSteering, hasTitleBasedSteering, promotionOffer, catalogueItem, inPromotionCatalogue, inLists, activateLowPerfExp, lowPerfExpVariant, activatePrimeSQTV, primeSQTVExpVariant, isOverProviderTitle, sponsoredAd, isShadowCampaign, hasActiveSRCampaign, seoCampaignConfig, clientCampaignConfig, isSeoCampaignFromConfig, isClientCampaignFromConfig, autoSwipeDelay, qtvAdditionalContexts, activatePPExperiments, autoSwipeSrCampaign, activateMxSqtvSr, mxSqtvSR, activateBrSqtvSr, brSqtvSR, doubleStackExpVariant, hardcodedShortNameDSExp, activateAtvPlacementsExp, atvPlacementsExpVariant, atvPlacementsExpPlacement, activateSwiperBrowserExp, swiperBrowserExpVariant, isBingeAuPromoTitle, freeTrialActive, hardcodedFTShortName, hardcodedQTVShortName, hardcodedSQTVShortName, hideTVODQTV, isSingleQTVActive, isSponsoredSqtvActive, qualityTvActive, freeTrialSwiperActive, adServerPlacement, adServerProviders, titleSourceId, placementFromAdServer, shortNameFromAdServer, hasPromotionFromAdServer, isValidAdServerPlacement, adServerRenderAction, overrideAdServerPlacement, adServerFlexContextValue, renderAdServerPromotion, isSQTVEmpty, showSQTV, promotionItems, hideSwiperControls, onClickOut, SraPlacement, SponsoredRecommendation, attachAdServerContexts, NewBuyBoxView }
}

})